/**
 * Screen Mapping Configuration
 *
 * Maps screen codes to their corresponding components and routes.
 * This is the central source of truth for all screen navigation in the claim portal.
 *
 * Screen Code Format: XX-YY-LABEL
 * - XX: Section number (00-05)
 * - YY: Screen number within section
 * - LABEL: Descriptive label
 *
 * Flow Indicators:
 * - M: Medical flow
 * - A: Annulation (Cancellation) flow
 * - B: Baggage flow
 * - No indicator: Common to all flows
 */

export interface ScreenConfig {
  component: string;
  route: string;
  section: string;
  flowSpecific?: 'MEDICAL' | 'CANCELLATION' | 'BAGGAGE' | 'APPEND_EXPENSES' | 'APPEND_DOCUMENTS';
  /**
   * Opt OUT of the "common screens" list returned by `getCommonScreens()`.
   *
   * `ClaimTypeScreen` uses `getCommonScreens().length` as the denominator of the
   * progress it displays, so registering a screen here normally bumps the progress
   * total FOR EVERY TENANT. A screen that must stay invisible to progression
   * (ADR-061: section-`00` screens are not workflow steps) sets this flag.
   *
   * Deliberately an explicit per-entry opt-out rather than a `section === 'entry'`
   * filter: the section-00 screens already registered are counted today, and
   * excluding them wholesale would change the totals currently shipped.
   */
  excludeFromProgress?: boolean;
}

export const SCREEN_MAPPING: Record<string, ScreenConfig> = {
  // Section 00: Entry Points
  '00-00-ACCUEIL': {
    component: 'HomeScreen',
    route: '/',
    section: 'entry'
  },
  '00-01-NOUVELLE': {
    component: 'PrepareClaimScreen',
    route: '/claim/00-01-NOUVELLE',
    section: 'entry'
  },
  '00-00-SELECTION': {
    component: 'EventSelectionScreen',
    route: '/claim/00-00-SELECTION',
    section: 'entry'
  },
  '00-02-AVIS': {
    component: 'LegalNoticeScreen',
    route: '/claim/00-02-AVIS',
    section: 'entry'
  },
  // Unconditional (DEV-7682): shown to every tenant between 00-02 and 01-00.
  // `excludeFromProgress` keeps it out of the progress denominator (ADR-061).
  '00-03-EVENEMENT': {
    component: 'EventDateScreen',
    route: '/claim/00-03-EVENEMENT',
    section: 'entry',
    excludeFromProgress: true
  },
  '00-10-RECLAMATIONS': {
    component: 'ClaimDetailsScreen',
    route: '/claim/00-10-RECLAMATIONS',
    section: 'entry'
  },

  // Section 01: Contract & Benefits (Common to all flows)
  '01-00-DEMANDEUR': {
    component: 'ContactPreferencesScreen',
    route: '/claim/01-00-DEMANDEUR',
    section: 'contract'
  },
  '01-01-CONTRAT': {
    component: 'ContractSelectionScreen',
    route: '/claim/01-01-CONTRAT',
    section: 'contract'
  },
  '01-02-AUTRES-CONTRATS': {
    component: 'OtherInsuranceScreen',
    route: '/claim/01-02-AUTRES-CONTRATS',
    section: 'contract'
  },
  // Conditional (DEV-7479): skipped when credit-card coverage was declared on
  // 01-02 AND the trip was paid with that card. See utils/tripPaymentScreenVisibility.
  '01-03-PAIEMENT-VOYAGE': {
    component: 'TripPaymentMethodScreen',
    route: '/claim/01-03-PAIEMENT-VOYAGE',
    section: 'contract'
  },

  // Section 02: Claim Type Selection & Flow Branching
  '02-00-GARANTIE': {
    component: 'ClaimTypeScreen',
    route: '/claim/02-00-GARANTIE',
    section: 'claimType'
  },

  // Section 02M: Medical Flow - Beneficiary & Travel
  '02M-01-BENEFICIAIRE': {
    component: 'BeneficiaryScreen',
    route: '/claim/02M-01-BENEFICIAIRE',
    section: 'beneficiary',
    flowSpecific: 'MEDICAL'
  },
  '02M-02-VOYAGE': {
    component: 'TravelConfirmationScreen',
    route: '/claim/02M-02-VOYAGE',
    section: 'travel',
    flowSpecific: 'MEDICAL'
  },

  // Section 02A: Cancellation Flow - Beneficiary & Travel
  '02A-01-BENEFICIAIRE': {
    component: 'BeneficiaryScreenCancellation',
    route: '/claim/02A-01-BENEFICIAIRE',
    section: 'beneficiary',
    flowSpecific: 'CANCELLATION'
  },
  '02A-02-VOYAGE': {
    component: 'TravelCancellationScreen',
    route: '/claim/02A-02-VOYAGE',
    section: 'travel',
    flowSpecific: 'CANCELLATION'
  },

  // Section 02B: Baggage Flow - Beneficiary & Travel
  '02B-01-BENEFICIAIRE': {
    component: 'BeneficiaryScreenBaggage',
    route: '/claim/02B-01-BENEFICIAIRE',
    section: 'beneficiary',
    flowSpecific: 'BAGGAGE'
  },
  '02B-02-VOYAGE': {
    component: 'TravelConfirmationScreenBaggage',
    route: '/claim/02B-02-VOYAGE',
    section: 'travel',
    flowSpecific: 'BAGGAGE'
  },

  // Section 03M: Medical Flow - Event Details
  '03M-00-ACCIDENT': {
    component: 'SpecificVerificationsScreen',
    route: '/claim/03M-00-ACCIDENT',
    section: 'eventDetails',
    flowSpecific: 'MEDICAL'
  },
  '03M-01-DETAILS': {
    component: 'EventDetailsScreen',
    route: '/claim/03M-01-DETAILS',
    section: 'eventDetails',
    flowSpecific: 'MEDICAL'
  },
  '03M-02-VERIFICATIONS': {
    component: 'MedicalVerificationScreen',
    route: '/claim/03M-02-VERIFICATIONS',
    section: 'eventDetails',
    flowSpecific: 'MEDICAL'
  },

  // Section 03A: Cancellation Flow - Event Details
  '03A-01-DETAILS1': {
    component: 'CancellationDetailsScreen1',
    route: '/claim/03A-01-DETAILS1',
    section: 'eventDetails',
    flowSpecific: 'CANCELLATION'
  },
  '03A-01-DETAILS2': {
    component: 'CancellationDetailsScreen2',
    route: '/claim/03A-01-DETAILS2',
    section: 'eventDetails',
    flowSpecific: 'CANCELLATION'
  },
  '03A-01-DETAILS3': {
    component: 'CancellationDetailsScreen3',
    route: '/claim/03A-01-DETAILS3',
    section: 'eventDetails',
    flowSpecific: 'CANCELLATION'
  },
  '03A-01-DETAILS4': {
    component: 'CancellationDetailsScreen4',
    route: '/claim/03A-01-DETAILS4',
    section: 'eventDetails',
    flowSpecific: 'CANCELLATION'
  },
  '03A-01-DETAILS5': {
    component: 'CancellationDetailsScreen5',
    route: '/claim/03A-01-DETAILS5',
    section: 'eventDetails',
    flowSpecific: 'CANCELLATION'
  },
  '03A-02-VERIFICATIONS': {
    component: 'CancellationMedicalVerificationScreen',
    route: '/claim/03A-02-VERIFICATIONS',
    section: 'eventDetails',
    flowSpecific: 'CANCELLATION'
  },

  // Section 03B: Baggage Flow - Event Details
  '03B-01-DETAILS': {
    component: 'BaggageDetailsScreen',
    route: '/claim/03B-01-DETAILS',
    section: 'eventDetails',
    flowSpecific: 'BAGGAGE'
  },
  '03B-02-VERIFICATIONS': {
    component: 'BaggageVerificationsScreen',
    route: '/claim/03B-02-VERIFICATIONS',
    section: 'eventDetails',
    flowSpecific: 'BAGGAGE'
  },

  // Section 04M: Medical Flow - Reimbursement
  '04M-00-REMBOURSEMENT': {
    component: 'ReimbursementDetailsScreen',
    route: '/claim/04M-00-REMBOURSEMENT',
    section: 'reimbursement',
    flowSpecific: 'MEDICAL'
  },

  // Section 04B: Baggage Flow - Reimbursement
  '04B-00-REMBOURSEMENT': {
    component: 'BaggageCategoryScreen',
    route: '/claim/04B-00-REMBOURSEMENT',
    section: 'documents',
    flowSpecific: 'BAGGAGE'
  },

  // Section 04: Common Document Screens (all flows)
  '04-01-FACTURES': {
    component: 'InvoiceUploadScreen',
    route: '/claim/04-01-FACTURES',
    section: 'documents'
  },
  '04-02-VALIDATION': {
    component: 'ExpenseValidationScreen',
    route: '/claim/04-02-VALIDATION',
    section: 'documents'
  },
  '04-03-JUSTIFICATIFS': {
    component: 'SupportingDocumentsScreen',
    route: '/claim/04-03-JUSTIFICATIFS',
    section: 'documents'
  },

  // Section 04E/06: Append Expenses Flow (DEV-6623)
  '06-01-NOUVEAUFRAIS': {
    component: 'NewExpensesIntroScreen',
    route: '/claim/06-01-NOUVEAUFRAIS',
    section: 'append-expenses',
    flowSpecific: 'APPEND_EXPENSES'
  },
  '04E-00-REMBOURSEMENT': {
    component: 'AppendReimbursementDetailsScreen',
    route: '/claim/04E-00-REMBOURSEMENT',
    section: 'append-expenses',
    flowSpecific: 'APPEND_EXPENSES'
  },
  '04E-01-FACTURES': {
    component: 'AppendInvoiceUploadScreen',
    route: '/claim/04E-01-FACTURES',
    section: 'append-expenses',
    flowSpecific: 'APPEND_EXPENSES'
  },
  '04E-02-VALIDATION': {
    component: 'AppendExpenseValidationScreen',
    route: '/claim/04E-02-VALIDATION',
    section: 'append-expenses',
    flowSpecific: 'APPEND_EXPENSES'
  },
  '04E-03-JUSTIFICATIFS': {
    component: 'AppendSupportingDocumentsScreen',
    route: '/claim/04E-03-JUSTIFICATIFS',
    section: 'append-expenses',
    flowSpecific: 'APPEND_EXPENSES'
  },

  // Section 06: Append Documents Flow (append-document)
  '06-00-NOUVEAUDOCUMENT': {
    component: 'AppendNewDocumentsScreen',
    route: '/claim/06-00-NOUVEAUDOCUMENT',
    section: 'append-documents',
    flowSpecific: 'APPEND_DOCUMENTS'
  },

  // Section 05: Final Steps (common to all flows)
  '05-01-PAIEMENT': {
    component: 'BankingInfoScreen',
    route: '/claim/05-01-PAIEMENT',
    section: 'submission'
  },
  '05M-02-MANDAT': {
    component: 'MandateSignatureScreen',
    route: '/claim/05M-02-MANDAT',
    section: 'submission',
    flowSpecific: 'MEDICAL'
  },
  '05-03-FIN': {
    component: 'ConfirmationScreen',
    route: '/claim/05-03-FIN',
    section: 'submission'
  }
};

/**
 * Helper function to get screen config by code
 */
export const getScreenConfig = (screenCode: string): ScreenConfig | undefined => {
  return SCREEN_MAPPING[screenCode];
};

/**
 * Helper function to get route by screen code
 */
export const getRouteByScreenCode = (screenCode: string): string | undefined => {
  return SCREEN_MAPPING[screenCode]?.route;
};

/**
 * Helper function to get all screens for a specific flow
 */
export const getScreensByFlow = (flow: 'MEDICAL' | 'CANCELLATION' | 'BAGGAGE'): Record<string, ScreenConfig> => {
  return Object.entries(SCREEN_MAPPING).reduce((acc, [code, config]) => {
    if (!config.flowSpecific || config.flowSpecific === flow) {
      acc[code] = config;
    }
    return acc;
  }, {} as Record<string, ScreenConfig>);
};

/**
 * Helper function to get all common screens (not flow-specific)
 *
 * `ClaimTypeScreen` derives the progress total it displays from the LENGTH of this
 * record, so any entry added here changes the progression of every tenant. Screens
 * flagged `excludeFromProgress` are therefore left out (ADR-061).
 */
export const getCommonScreens = (): Record<string, ScreenConfig> => {
  return Object.entries(SCREEN_MAPPING).reduce((acc, [code, config]) => {
    if (!config.flowSpecific && !config.excludeFromProgress) {
      acc[code] = config;
    }
    return acc;
  }, {} as Record<string, ScreenConfig>);
};

/**
 * Get all screen codes as an array
 */
export const getAllScreenCodes = (): string[] => {
  return Object.keys(SCREEN_MAPPING);
};

/**
 * Check if a screen code exists
 */
export const isValidScreenCode = (code: string): boolean => {
  return code in SCREEN_MAPPING;
};
